import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Spinner } from '../../../components/ui/spinner';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { ArrowUpCircle, ArrowDownCircle, Calendar, CheckCircle, CreditCard, Crown, Phone, Mail, AlertTriangle, Clock, Info, Copy, Check, RefreshCw, Tag, Loader2, X, ShieldCheck } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYMENT_NUMBER = '01786264886';

export default function SubscriptionTab() {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [renewStep, setRenewStep] = useState(1);
  const [renewCycle, setRenewCycle] = useState('monthly');
  const [renewMethod, setRenewMethod] = useState('bkash');
  const [renewTrxId, setRenewTrxId] = useState('');
  const [renewPhone, setRenewPhone] = useState('');
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [confettiPieces, setConfettiPieces] = useState([]);
  // Voucher state for renewal
  const [renewVoucherCode, setRenewVoucherCode] = useState('');
  const [renewVoucherApplied, setRenewVoucherApplied] = useState(null);
  const [renewVoucherLoading, setRenewVoucherLoading] = useState(false);
  const [renewVoucherError, setRenewVoucherError] = useState('');

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/subscription/my`);
      setSubscription(data);
    } catch (err) {
      setSubscription(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'trialing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'expired': return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    const map = { active: 'sub_active', trialing: 'sub_trialing', expired: 'sub_expired', cancelled: 'sub_cancelled' };
    return t(map[status] || 'sub_no_plan');
  };

  const getDaysRemaining = () => {
    if (!subscription?.subscription_end_date) return null;
    const end = new Date(subscription.subscription_end_date);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  const getBillingCycleLabel = (cycle) => {
    if (cycle === 'half-yearly') return language === 'en' ? 'Half-Yearly' : 'অর্ধ-বার্ষিক';
    if (cycle === 'yearly') return language === 'en' ? 'Yearly' : 'বার্ষিক';
    return language === 'en' ? 'Monthly' : 'মাসিক';
  };

  const copyPaymentNumber = () => {
    navigator.clipboard.writeText(PAYMENT_NUMBER);
    setCopiedNumber(true);
    setTimeout(() => setCopiedNumber(false), 2000);
  };

  const getRenewalPrice = (plan, cycle) => {
    if (!plan) return 0;
    const mp = plan.monthly_price || plan.discounted_price_monthly || plan.regular_price_monthly || 0;
    const hp = plan.half_yearly_price || plan.discounted_price_half_yearly || plan.regular_price_half_yearly || 0;
    const yp = plan.yearly_price || plan.discounted_price_yearly || plan.regular_price_yearly || 0;
    if (cycle === 'yearly') return yp || mp * 10;
    if (cycle === 'half-yearly') return hp || mp * 5;
    return mp;
  };

  const openRenewDialog = () => {
    setRenewStep(1);
    setRenewCycle(subscription.subscription_record?.billing_cycle || 'monthly');
    setRenewMethod('bkash');
    setRenewTrxId('');
    setRenewPhone('');
    setRenewVoucherCode('');
    setRenewVoucherApplied(null);
    setRenewVoucherError('');
    setShowRenewDialog(true);
  };

  const applyRenewVoucher = async () => {
    if (!renewVoucherCode.trim()) return;
    setRenewVoucherLoading(true);
    setRenewVoucherError('');
    try {
      const plan = subscription.current_package?.slug || subscription.subscription_plan;
      const res = await axios.post(`${API}/vouchers/validate`, {
        code: renewVoucherCode.trim().toUpperCase(),
        plan,
      });
      setRenewVoucherApplied({ ...res.data, code: renewVoucherCode.trim().toUpperCase() });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setRenewVoucherError(typeof detail === 'string' ? detail : 'Invalid voucher');
      setRenewVoucherApplied(null);
    } finally { setRenewVoucherLoading(false); }
  };

  const removeRenewVoucher = () => {
    setRenewVoucherApplied(null);
    setRenewVoucherCode('');
    setRenewVoucherError('');
  };

  const submitRenewal = async () => {
    if (!renewTrxId.trim()) {
      toast({ title: language === 'en' ? 'Transaction ID required' : 'ট্রানজেকশন আইডি প্রয়োজন', variant: 'destructive' });
      return;
    }
    setRenewSubmitting(true);
    try {
      const currentPlan = subscription.available_plans?.find(p => p.slug === (subscription.current_package?.slug || subscription.subscription_plan)) || subscription.current_package;
      const amount = getRenewalPrice(currentPlan, renewCycle);
      const discountAmt = renewVoucherApplied ? Math.round(amount * renewVoucherApplied.discount_percent / 100) : 0;
      const finalAmount = amount - discountAmt;
      const endDate = subscription.subscription_end_date ? new Date(subscription.subscription_end_date) : new Date();
      let nextEnd;
      const base = endDate > new Date() ? endDate : new Date();
      if (renewCycle === 'yearly') nextEnd = new Date(base.getTime() + 365 * 24 * 60 * 60 * 1000);
      else if (renewCycle === 'half-yearly') nextEnd = new Date(base.getTime() + 180 * 24 * 60 * 60 * 1000);
      else nextEnd = new Date(base.getTime() + 30 * 24 * 60 * 60 * 1000);

      await axios.post(`${API}/renewals`, {
        billing_cycle: renewCycle,
        payment_method: renewMethod,
        transaction_id: renewTrxId,
        payment_phone: renewPhone,
        amount: amount,
        voucher_code: renewVoucherApplied?.code || null,
      });
      setShowRenewDialog(false);
      // confetti
      const pieces = Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        color: ['#10b981', '#14b8a6', '#0ea5e9', '#6366f1', '#f59e0b', '#ec4899'][Math.floor(Math.random() * 6)],
        size: Math.random() * 8 + 4,
      }));
      setConfettiPieces(pieces);
      setTimeout(() => setConfettiPieces([]), 4000);
      setSuccessData({
        plan: currentPlan?.name || subscription.subscription_plan,
        cycle: getBillingCycleLabel(renewCycle),
        amount: finalAmount,
        nextBill: nextEnd.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
      });
      setShowSuccessPopup(true);
    } catch (err) {
      toast({ title: language === 'en' ? 'Failed to submit renewal' : 'রিনিউয়াল জমা দিতে ব্যর্থ', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
    setRenewSubmitting(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  if (!subscription) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Crown className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{language === 'en' ? 'Unable to load subscription details' : 'সাবস্ক্রিপশন তথ্য লোড করা যায়নি'}</p>
          <Button variant="outline" className="mt-3" onClick={fetchSubscription}>
            {language === 'en' ? 'Retry' : 'আবার চেষ্টা করুন'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const daysRemaining = getDaysRemaining();
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
  const isExpired = daysRemaining !== null && daysRemaining <= 0;

  // Trial period calculation
  const isInTrial = subscription?.trial_end_date && new Date(subscription.trial_end_date) > new Date();
  const trialDaysLeft = subscription?.trial_end_date
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="space-y-6">
      {/* COMMENTED OUT: 15-day Trial period + Money-back guarantee
      {isInTrial && (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
            <Clock className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-slate-500">
                {language === 'en'
                  ? `Free Trial Active — ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining`
                  : `ফ্রি ট্রায়াল চলছে — ${trialDaysLeft} দিন বাকি`}
              </p>
              <p className="text-[11px] mt-0.5 text-slate-400">
                {language === 'en'
                  ? '15-day free trial. Full access to all features.'
                  : '১৫ দিনের ফ্রি ট্রায়াল। সকল ফিচারে সম্পূর্ণ অ্যাক্সেস।'}
              </p>
            </div>
          </div>

          {subscription?.money_back_guarantee && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
              <ShieldCheck className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-slate-500">
                  {language === 'en' ? 'Money-Back Guarantee' : 'মানি-ব্যাক গ্যারান্টি'}
                </p>
                <p className="text-[11px] mt-0.5 text-slate-400">
                  {language === 'en'
                    ? 'Full refund within trial period. No questions asked.'
                    : 'ট্রায়াল পিরিয়ডে সম্পূর্ণ রিফান্ড।'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      */}

      {/* Free trial active banner (30-day) */}
      {isInTrial && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg border border-emerald-200 bg-emerald-50/60">
          <Clock className="w-4 h-4 shrink-0 text-emerald-500 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-emerald-700">
              {language === 'en'
                ? `Free Trial Active — ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining`
                : `ফ্রি ট্রায়াল চলছে — ${trialDaysLeft} দিন বাকি`}
            </p>
            <p className="text-[11px] mt-0.5 text-emerald-600/70">
              {language === 'en'
                ? 'Full access to all features during your trial period.'
                : 'ট্রায়াল পিরিয়ডে সকল ফিচারে সম্পূর্ণ অ্যাক্সেস।'}
            </p>
          </div>
        </div>
      )}

      {/* Expiry warning banner */}
      {(isExpiringSoon || isExpired) && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${isExpired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <AlertTriangle className={`w-5 h-5 shrink-0 ${isExpired ? 'text-red-500' : 'text-amber-500'}`} />
          <div className="flex-1">
            <p className={`text-sm font-bold ${isExpired ? 'text-red-700' : 'text-amber-700'}`}>
              {isExpired
                ? (isInTrial
                  ? (language === 'en' ? 'Your free trial has expired!' : 'আপনার ফ্রি ট্রায়াল মেয়াদোত্তীর্ণ!')
                  : (language === 'en' ? 'Your subscription has expired!' : 'আপনার সাবস্ক্রিপশন মেয়াদোত্তীর্ণ!'))
                : (isInTrial
                  ? (language === 'en' ? `Your free trial expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}` : `আপনার ফ্রি ট্রায়াল ${daysRemaining} দিনে মেয়াদোত্তীর্ণ হবে`)
                  : (language === 'en' ? `Your subscription expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}` : `আপনার সাবস্ক্রিপশন ${daysRemaining} দিনে মেয়াদোত্তীর্ণ হবে`))
              }
            </p>
            <p className={`text-xs mt-0.5 ${isExpired ? 'text-red-500' : 'text-amber-500'}`}>
              {isInTrial
                ? (language === 'en' ? 'Start a paid plan below to keep using all features.' : 'সকল ফিচার ব্যবহার চালিয়ে যেতে নিচে পেইড প্ল্যান শুরু করুন।')
                : (language === 'en' ? 'Renew your plan below or contact us: 01786264886' : 'নিচে প্ল্যান রিনিউ করুন অথবা যোগাযোগ করুন: ০১৭৮৬২৬৪৮৮৬')}
            </p>
          </div>
          <Button size="sm" className={isInTrial ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'} onClick={openRenewDialog}>
            {isInTrial ? (language === 'en' ? 'Start Paid Plan' : 'পেইড প্ল্যান শুরু করুন') : (language === 'en' ? 'Renew Now' : 'রিনিউ করুন')}
          </Button>
        </div>
      )}

      {/* Current Plan Card */}
      <Card className="shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-blue-100">
                <Crown className="w-4 h-4 text-blue-800" />
              </div>
              {t('sub_current_plan')}
            </CardTitle>
          </CardHeader>
        </div>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_current_plan')}</div>
              <div className="text-lg font-[800] text-blue-800 capitalize">
                {subscription.current_package
                  ? (language === 'bn' && subscription.current_package.name_bn
                    ? subscription.current_package.name_bn
                    : subscription.current_package.name)
                  : subscription.subscription_plan || '—'}
              </div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_status')}</div>
              <Badge className={`mt-1 ${getStatusColor(subscription.subscription_status)}`}>
                {getStatusLabel(subscription.subscription_status)}
              </Badge>
            </div>
            {subscription.subscription_end_date && (
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{language === 'en' ? 'Expires' : 'মেয়াদ'}</div>
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {new Date(subscription.subscription_end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                {daysRemaining !== null && daysRemaining > 0 && (
                  <div className="text-[10px] text-slate-400 mt-0.5">{daysRemaining} {language === 'en' ? 'days left' : 'দিন বাকি'}</div>
                )}
              </div>
            )}
            {subscription.created_at && (
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_member_since')}</div>
                <div className="text-sm font-semibold">{new Date(subscription.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              </div>
            )}
          </div>

          {subscription.current_package?.features?.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('sub_features')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(language === 'bn' && subscription.current_package.features_bn?.length
                  ? subscription.current_package.features_bn
                  : subscription.current_package.features
                ).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing & Payment Info */}
      {subscription.subscription_record && (
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-100">
                <CreditCard className="w-4 h-4 text-emerald-700" />
              </div>
              {t('sub_billing_info')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {subscription.subscription_record.billing_cycle && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_billing_cycle')}</div>
                  <div className="text-sm font-semibold">{getBillingCycleLabel(subscription.subscription_record.billing_cycle)}</div>
                </div>
              )}
              {subscription.subscription_record.payment_method && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_payment_method')}</div>
                  <div className="text-sm font-semibold capitalize">{subscription.subscription_record.payment_method}</div>
                </div>
              )}
              {(subscription.subscription_record.total_amount || subscription.subscription_record.amount) && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_amount')}</div>
                  <div className="text-sm font-bold text-blue-800">৳{(subscription.subscription_record.total_amount || subscription.subscription_record.amount).toLocaleString()}</div>
                </div>
              )}
              {subscription.subscription_record.payment_status && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('sub_payment_status')}</div>
                  <Badge className={`mt-0.5 ${subscription.subscription_record.payment_status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {subscription.subscription_record.payment_status}
                  </Badge>
                </div>
              )}
              {subscription.subscription_end_date && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{language === 'en' ? 'Next Payment Due' : 'পরবর্তী পেমেন্ট'}</div>
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    {new Date(subscription.subscription_end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              )}
              {subscription.subscription_record.created_at && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{language === 'en' ? 'Last Payment' : 'শেষ পেমেন্ট'}</div>
                  <div className="text-sm font-semibold">{new Date(subscription.subscription_record.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                </div>
              )}
            </div>

            {/* Manual billing info box */}
            <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-800">{language === 'en' ? 'Manual Billing' : 'ম্যানুয়াল বিলিং'}</p>
                <p className="text-xs text-blue-600 mt-1">{language === 'en'
                  ? 'All payments are processed manually. We will contact and remind you before your next payment is due. Please keep your phone number up to date.'
                  : 'সকল পেমেন্ট ম্যানুয়ালি প্রসেস করা হয়। পরবর্তী পেমেন্টের আগে আমরা আপনাকে যোগাযোগ করে রিমাইন্ড করব।'
                }</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-blue-500">
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 01786264886</span>
                  <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> contact@motopotti.com</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans with Upgrade/Downgrade */}
      {subscription.available_plans?.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-purple-100">
                <ArrowUpCircle className="w-4 h-4 text-purple-700" />
              </div>
              {t('sub_available_plans')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {subscription.available_plans.map((plan) => {
                const currentSlug = subscription.current_package?.slug || subscription.subscription_plan;
                const isCurrent = plan.slug === currentSlug;
                const currentPlanOrder = subscription.available_plans.findIndex(p => p.slug === currentSlug);
                const thisPlanOrder = subscription.available_plans.findIndex(p => p.slug === plan.slug);
                const isUpgrade = thisPlanOrder > currentPlanOrder;
                return (
                  <div
                    key={plan.slug}
                    className={`relative rounded-xl border-2 p-5 flex flex-col transition-all ${
                      isCurrent
                        ? 'border-slate-200 bg-white hover:border-blue-400 hover:shadow-md'
                        : plan.is_popular
                          ? 'border-emerald-400 bg-white hover:border-emerald-500 hover:shadow-md'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    {plan.is_popular && (
                      <Badge className="absolute -top-2.5 right-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs">
                        {t('sub_popular')}
                      </Badge>
                    )}
                    <h4 className="font-bold text-lg mb-1">
                      {language === 'bn' && plan.name_bn ? plan.name_bn : plan.name}
                    </h4>
                    {plan.tagline && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {language === 'bn' && plan.tagline_bn ? plan.tagline_bn : plan.tagline}
                      </p>
                    )}
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-[900] text-blue-800">৳{plan.monthly_price?.toLocaleString()}</span>
                        <span className="text-sm font-normal text-muted-foreground">/{t('sub_monthly')}</span>
                      </div>
                      {plan.half_yearly_price > 0 && (
                        <p className="text-sm text-muted-foreground">
                          ৳{plan.half_yearly_price.toLocaleString()}/{language === 'en' ? '6 months' : '৬ মাস'}
                        </p>
                      )}
                      {plan.yearly_price > 0 && (
                        <p className="text-sm text-muted-foreground">
                          ৳{plan.yearly_price.toLocaleString()}/{t('sub_yearly')}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-4 flex-1">
                      {(language === 'bn' && plan.features_bn?.length ? plan.features_bn : plan.features).map((f, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-auto pt-2">
                    {isCurrent ? (
                      <Button
                        variant="outline"
                        className={`w-full ${isInTrial ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                        onClick={openRenewDialog}
                      >
                        {isInTrial
                          ? <><CreditCard className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Start Paid Plan' : 'পেইড প্ল্যান শুরু করুন'}</>
                          : <><RefreshCw className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Renew Plan' : 'রিনিউ করুন'}</>}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className={`w-full ${isUpgrade ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                        onClick={() => { setSelectedPlan(plan); setShowUpgradeDialog(true); }}
                      >
                        {isUpgrade ? (
                          <><ArrowUpCircle className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Upgrade' : 'আপগ্রেড'}</>
                        ) : (
                          <><ArrowDownCircle className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Downgrade' : 'ডাউনগ্রেড'}</>
                        )}
                      </Button>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade/Downgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {language === 'en' ? 'Change Subscription Plan' : 'সাবস্ক্রিপশন প্ল্যান পরিবর্তন'}
            </DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-sm text-slate-500">{language === 'en' ? 'Switch to' : 'পরিবর্তন করুন'}</p>
                <p className="text-xl font-[800] text-slate-900 mt-1">{language === 'bn' && selectedPlan.name_bn ? selectedPlan.name_bn : selectedPlan.name}</p>
                <p className="text-sm text-blue-700 font-bold mt-1">৳{selectedPlan.monthly_price?.toLocaleString()}/{t('sub_monthly')}</p>
              </div>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-left flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-amber-800 font-semibold">{language === 'en' ? 'Manual Process' : 'ম্যানুয়াল প্রক্রিয়া'}</p>
                  <p className="text-xs text-amber-600 mt-1">
                    {language === 'en'
                      ? 'Plan changes are processed manually and may take up to 24 hours. Our team will contact you to finalize the switch and handle any billing adjustments.'
                      : 'প্ল্যান পরিবর্তন ম্যানুয়ালি প্রসেস করা হয় এবং ২৪ ঘণ্টা পর্যন্ত সময় নিতে পারে। আমাদের টিম আপনার সাথে যোগাযোগ করবে।'
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 01786264886</span>
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> contact@motopotti.com</span>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowUpgradeDialog(false)}>
                  {language === 'en' ? 'Cancel' : 'বাতিল'}
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                  setShowUpgradeDialog(false);
                  const currentName = subscription.current_package?.name || subscription.subscription_plan;
                  const garageName = subscription.tenant_name || '';
                  const garageEmail = subscription.tenant_email || '';
                  const garagePhone = subscription.tenant_phone || '';
                  window.open(`https://wa.me/8801786264886?text=${encodeURIComponent(
                    `Hi, I want to change my MotoPotti subscription to ${selectedPlan.name}.\n\nGarage: ${garageName}\nCurrent Plan: ${currentName}\nEmail: ${garageEmail}\nPhone: ${garagePhone}`
                  )}`, '_blank');
                }}>
                  <Phone className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Contact Us' : 'যোগাযোগ করুন'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={showRenewDialog} onOpenChange={(open) => { if (!renewSubmitting) setShowRenewDialog(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              {isInTrial
                ? <><CreditCard className="w-5 h-5 text-emerald-600" />{language === 'en' ? 'Start Paid Plan' : 'পেইড প্ল্যান শুরু করুন'}</>
                : <><RefreshCw className="w-5 h-5 text-blue-600" />{language === 'en' ? 'Renew Subscription' : 'সাবস্ক্রিপশন রিনিউ'}</>}
            </DialogTitle>
          </DialogHeader>
          {renewStep === 1 && (() => {
            const currentPlan = subscription.available_plans?.find(p => p.slug === (subscription.current_package?.slug || subscription.subscription_plan)) || subscription.current_package;
            const price = getRenewalPrice(currentPlan, renewCycle);
            return (
              <div className="space-y-4 py-2">
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-xs text-blue-500 font-semibold uppercase tracking-wider">{isInTrial ? (language === 'en' ? 'Starting Paid Plan' : 'পেইড প্ল্যান শুরু হচ্ছে') : (language === 'en' ? 'Renewing' : 'রিনিউ করছেন')}</p>
                  <p className="text-lg font-[800] text-blue-800 mt-1">{currentPlan?.name || subscription.subscription_plan}</p>
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{language === 'en' ? 'Billing Cycle' : 'বিলিং সাইকেল'}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {['monthly', 'half-yearly', 'yearly'].map(c => (
                      <button
                        key={c}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${renewCycle === c ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-blue-300'}`}
                        onClick={() => setRenewCycle(c)}
                      >
                        <div className="text-xs font-bold">{getBillingCycleLabel(c)}</div>
                        <div className="text-sm font-[800] text-blue-800 mt-0.5">৳{getRenewalPrice(currentPlan, c)?.toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Voucher / Coupon Section */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Voucher Code' : 'ভাউচার কোড'}</p>
                  {renewVoucherApplied ? (
                    <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-dashed border-emerald-400 bg-emerald-50">
                      <Tag className="w-4 h-4 text-emerald-600" />
                      <span className="flex-1 text-sm font-bold text-emerald-700">{renewVoucherApplied.code} &mdash; {renewVoucherApplied.discount_percent}% {language === 'en' ? 'off' : 'ছাড়'}</span>
                      <button onClick={removeRenewVoucher} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={renewVoucherCode}
                        onChange={e => setRenewVoucherCode(e.target.value.toUpperCase())}
                        placeholder={language === 'en' ? 'Enter code' : 'কোড লিখুন'}
                        className="flex-1 px-3 py-2 text-sm rounded-xl border-2 border-dashed border-slate-300 focus:border-blue-400 focus:outline-none font-mono tracking-wider"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl"
                        disabled={!renewVoucherCode.trim() || renewVoucherLoading}
                        onClick={() => applyRenewVoucher(currentPlan?.slug || subscription.subscription_plan)}
                      >
                        {renewVoucherLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'en' ? 'Apply' : 'প্রয়োগ')}
                      </Button>
                    </div>
                  )}
                  {renewVoucherError && <p className="text-xs text-red-500">{renewVoucherError}</p>}
                </div>

                <div className="p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600">{language === 'en' ? 'Total Amount' : 'মোট পরিমাণ'}</span>
                    {renewVoucherApplied ? (
                      <div className="text-right">
                        <span className="text-sm text-slate-400 line-through mr-2">৳{price?.toLocaleString()}</span>
                        <span className="text-xl font-[900] text-emerald-700">৳{Math.round(price * (1 - renewVoucherApplied.discount_percent / 100))?.toLocaleString()}</span>
                      </div>
                    ) : (
                      <span className="text-xl font-[900] text-blue-800">৳{price?.toLocaleString()}</span>
                    )}
                  </div>
                </div>

                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setRenewStep(2)}>
                  {language === 'en' ? 'Continue to Payment' : 'পেমেন্ট করুন'} →
                </Button>
              </div>
            );
          })()}

          {renewStep === 2 && (
            <div className="space-y-4 py-2">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{language === 'en' ? 'Payment Method' : 'পেমেন্ট পদ্ধতি'}</p>
                <div className="grid grid-cols-3 gap-2">
                  {[{id:'bkash', label:'bKash', color:'bg-pink-50 border-pink-300 text-pink-700'}, {id:'nagad', label:'Nagad', color:'bg-orange-50 border-orange-300 text-orange-700'}, {id:'manual', label: language === 'en' ? 'Manual' : 'ম্যানুয়াল', color:'bg-slate-50 border-slate-300 text-slate-700'}].map(m => (
                    <button key={m.id} className={`p-3 rounded-xl border-2 text-center text-sm font-bold transition-all ${renewMethod === m.id ? m.color + ' shadow-sm' : 'border-slate-200 hover:border-blue-300'}`} onClick={() => setRenewMethod(m.id)}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {renewMethod !== 'manual' && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-xs font-bold text-emerald-700 mb-2">{language === 'en' ? 'Send payment to:' : 'পেমেন্ট পাঠান:'}</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-lg font-[800] text-emerald-800 tracking-wider">{PAYMENT_NUMBER}</code>
                    <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={copyPaymentNumber}>
                      {copiedNumber ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-emerald-600 mt-1">{renewMethod === 'bkash' ? 'bKash' : 'Nagad'} • Personal</p>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Transaction ID' : 'ট্রানজেকশন আইডি'} *</label>
                <Input className="mt-1" placeholder={language === 'en' ? 'Enter TRX ID' : 'TRX আইডি লিখুন'} value={renewTrxId} onChange={e => setRenewTrxId(e.target.value)} />
              </div>

              {renewMethod !== 'manual' && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{language === 'en' ? 'Payment Phone (optional)' : 'পেমেন্ট ফোন (ঐচ্ছিক)'}</label>
                  <Input className="mt-1" placeholder="01XXX..." value={renewPhone} onChange={e => setRenewPhone(e.target.value)} />
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setRenewStep(1)} disabled={renewSubmitting}>
                  ← {language === 'en' ? 'Back' : 'পিছনে'}
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={submitRenewal} disabled={renewSubmitting}>
                  {renewSubmitting ? <Spinner size="sm" /> : <><CheckCircle className="w-4 h-4 mr-1.5" />{language === 'en' ? 'Submit Renewal' : 'রিনিউ জমা দিন'}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confetti */}
      {confettiPieces.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[9999]">
          {confettiPieces.map(p => (
            <div key={p.id} className="absolute" style={{
              left: `${p.left}%`, top: '-10px',
              width: `${p.size}px`, height: `${p.size}px`,
              backgroundColor: p.color, borderRadius: p.size > 8 ? '50%' : '2px',
              animation: `sub-confetti-fall ${2 + p.delay}s ease-in forwards`,
              animationDelay: `${p.delay}s`, opacity: 0.9,
            }} />
          ))}
          <style>{`@keyframes sub-confetti-fall { 0% { transform: translateY(0) rotate(0deg); opacity: 0.9; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
        </div>
      )}

      {/* Success Popup */}
      <Dialog open={showSuccessPopup} onOpenChange={setShowSuccessPopup}>
        <DialogContent className="max-w-sm text-center overflow-hidden p-0">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-6 text-white">
            <div className="w-14 h-14 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-3">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-[800]">{language === 'en' ? 'Renewal Submitted!' : 'রিনিউয়াল জমা হয়েছে!'}</h3>
            <p className="text-emerald-100 text-sm mt-1">{language === 'en' ? 'We will verify your payment within 24 hours' : '২৪ ঘণ্টার মধ্যে আমরা আপনার পেমেন্ট যাচাই করব'}</p>
          </div>
          {successData && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-left">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{language === 'en' ? 'Plan' : 'প্ল্যান'}</div>
                  <div className="text-sm font-bold mt-0.5">{successData.plan}</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-left">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{language === 'en' ? 'Billing' : 'বিলিং'}</div>
                  <div className="text-sm font-bold mt-0.5">{successData.cycle}</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-left">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{language === 'en' ? 'Amount' : 'পরিমাণ'}</div>
                  <div className="text-sm font-bold text-blue-800 mt-0.5">৳{successData.amount?.toLocaleString()}</div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-left">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{language === 'en' ? 'Next Bill' : 'পরবর্তী বিল'}</div>
                  <div className="text-sm font-bold mt-0.5">{successData.nextBill}</div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 01786264886</span>
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> contact@motopotti.com</span>
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => { setShowSuccessPopup(false); fetchSubscription(); }}>
                {language === 'en' ? 'Got it!' : 'ঠিক আছে!'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
